import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Select from '@radix-ui/react-select';
import { PET_CATEGORY_VALUES, PET_SUBTYPE_LABEL, type PetCategoryType } from '@/types/pet-category';
import { PET_SEX_VALUES, type PetSexType } from '@/types/pet-sex';
import type { DatePrecision } from '@/utils/dateFormatting';
import DateFieldWithPrecision from '@/components/shared/DateFieldWithPrecision';
import { petCategory } from '@/translations/en';
import { PET_OWNER_ROLE_LABELS } from '@/types/pet-owner';

export interface AddPetCreateFormState {
  displayName: string;
  category: PetCategoryType;
  subType: string;
  sex: PetSexType;
  birthDate: string;
  birthDatePrecision: DatePrecision;
  adoptionDate: string;
  adoptionDatePrecision: DatePrecision;
  deathDate: string;
  deathDatePrecision: DatePrecision;
  isDeceased: boolean;
}

export interface AddPetCreateNewFieldsProps {
  petFormData: AddPetCreateFormState;
  setPetFormData: React.Dispatch<React.SetStateAction<AddPetCreateFormState>>;
  errors: Record<string, string>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  isSubmitting: boolean;
  additionalOwners?: {
    rows: { ownerFamilyMemberId: string; displayName: string }[];
    choices: Record<string, { selected: boolean; role: 'primary' | 'secondary' | 'caregiver' }>;
    onToggle: (ownerFamilyMemberId: string, selected: boolean) => void;
    onRoleChange: (ownerFamilyMemberId: string, role: 'primary' | 'secondary' | 'caregiver') => void;
  };
}

const AddPetCreateNewFields: React.FC<AddPetCreateNewFieldsProps> = ({
  petFormData,
  setPetFormData,
  errors,
  setErrors,
  isSubmitting,
  additionalOwners
}) => {
  const { t } = useTranslation('forms');
  return (
    <div className="add-pet-screen__form">
      <div className="add-pet-screen__form-row">
        <div className="person-edit-form__field">
          <label className="person-edit-form__label" htmlFor="pet-displayName">
            Pet name <span className="person-edit-form__required">*</span>
          </label>
          <input
            id="pet-displayName"
            type="text"
            value={petFormData.displayName}
            onChange={(e) => {
              setPetFormData((p) => ({ ...p, displayName: e.target.value }));
              if (errors.displayName) setErrors((prev) => ({ ...prev, displayName: '' }));
            }}
            disabled={isSubmitting}
            className="person-edit-form__input"
            placeholder={t('pet.namePlaceholder')}
          />
          {errors.displayName && <span className="person-edit-form__error-message">{errors.displayName}</span>}
        </div>
        <div className="person-edit-form__field">
          <label className="person-edit-form__label" htmlFor="pet-category">
            {t('pet.category')} <span className="person-edit-form__required">*</span>
          </label>
          <Select.Root
            value={petFormData.category}
            onValueChange={(v) =>
              setPetFormData((p) => ({
                ...p,
                category: v as PetCategoryType
              }))
            }
            disabled={isSubmitting}
          >
            <Select.Trigger id="pet-category" className="form-select-trigger" aria-label={t('pet.categoryAria')}>
              <Select.Value />
            </Select.Trigger>
            <Select.Portal>
              <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                {PET_CATEGORY_VALUES.map((c) => (
                  <Select.Item key={c} value={c} className="form-select-item">
                    <Select.ItemText>{petCategory[c] ?? c}</Select.ItemText>
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Portal>
          </Select.Root>
        </div>
      </div>
      <div className="add-pet-screen__form-row">
        <div className="person-edit-form__field">
          <label className="person-edit-form__label" htmlFor="pet-subType">
            {PET_SUBTYPE_LABEL[petFormData.category]}
          </label>
          <input
            id="pet-subType"
            type="text"
            value={petFormData.subType}
            onChange={(e) => setPetFormData((p) => ({ ...p, subType: e.target.value }))}
            disabled={isSubmitting}
            className="person-edit-form__input"
            placeholder={
              ['dog', 'cat'].includes(petFormData.category)
                ? 'e.g. Golden Retriever'
                : ['aquatic'].includes(petFormData.category)
                  ? 'e.g. Goldfish, Betta'
                  : 'e.g. Hamster, Parakeet'
            }
          />
        </div>
        <div className="person-edit-form__field">
          <label className="person-edit-form__label" htmlFor="pet-sex">
            {t('pet.sex')}
          </label>
          <Select.Root
            value={petFormData.sex}
            onValueChange={(v) =>
              setPetFormData((p) => ({
                ...p,
                sex: v as PetSexType
              }))
            }
            disabled={isSubmitting}
          >
            <Select.Trigger id="pet-sex" className="form-select-trigger" aria-label={t('pet.sex')}>
              <Select.Value />
            </Select.Trigger>
            <Select.Portal>
              <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                {PET_SEX_VALUES.map((s) => (
                  <Select.Item key={s} value={s} className="form-select-item">
                    <Select.ItemText>{t(`petSex.${s}`)}</Select.ItemText>
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Portal>
          </Select.Root>
        </div>
      </div>
      <div className="add-pet-screen__form-row add-pet-screen__dates-row">
        <div className="person-edit-form__field">
          <DateFieldWithPrecision
            id="pet-birthDate"
            label={t('pet.birthDate')}
            value={petFormData.birthDate}
            precision={petFormData.birthDatePrecision}
            onValueChange={(v) => {
              setPetFormData((p) => ({ ...p, birthDate: v }));
              if (errors.birthDate) setErrors((prev) => ({ ...prev, birthDate: '' }));
            }}
            onPrecisionChange={(p) => setPetFormData((prev) => ({ ...prev, birthDatePrecision: p }))}
            onErrorClear={() => setErrors((prev) => ({ ...prev, birthDate: '' }))}
            disabled={isSubmitting}
            allowClearViaInput
            layout="inline"
            wrapperClassName="add-pet-screen__date-field"
          />
        </div>
        <div className="person-edit-form__field">
          <DateFieldWithPrecision
            id="pet-adoptionDate"
            label={t('pet.adoptionDate')}
            value={petFormData.adoptionDate}
            precision={petFormData.adoptionDatePrecision}
            onValueChange={(v) => {
              setPetFormData((p) => ({ ...p, adoptionDate: v }));
              if (errors.adoptionDate) setErrors((prev) => ({ ...prev, adoptionDate: '' }));
            }}
            onPrecisionChange={(p) => setPetFormData((prev) => ({ ...prev, adoptionDatePrecision: p }))}
            onErrorClear={() => setErrors((prev) => ({ ...prev, adoptionDate: '' }))}
            disabled={isSubmitting}
            allowClearViaInput
            layout="inline"
            wrapperClassName="add-pet-screen__date-field"
          />
        </div>
      </div>
      <div className="add-pet-screen__death-section">
        <div className="add-pet-screen__deceased-row">
          <label
            className="person-edit-form__label person-edit-form__checkbox-label add-pet-screen__deceased-label"
            htmlFor="pet-deceased"
          >
            <input
              id="pet-deceased"
              type="checkbox"
              checked={petFormData.isDeceased}
              onChange={(e) =>
                setPetFormData((p) => ({
                  ...p,
                  isDeceased: e.target.checked,
                  ...(e.target.checked ? {} : { deathDate: '' })
                }))
              }
              disabled={isSubmitting}
              className="person-edit-form__checkbox"
            />
            <span>{t('pet.deceased')}</span>
          </label>
          <span className="person-edit-form__hint add-pet-screen__deceased-hint">{t('pet.deceasedHint')}</span>
        </div>
        {petFormData.isDeceased && (
          <div className="add-pet-screen__form-row add-pet-screen__death-date-row">
            <div className="person-edit-form__field">
              <DateFieldWithPrecision
                id="pet-deathDate"
                label={t('pet.deathDate')}
                value={petFormData.deathDate}
                precision={petFormData.deathDatePrecision}
                onValueChange={(v) => {
                  setPetFormData((p) => ({ ...p, deathDate: v }));
                  if (errors.deathDate) setErrors((prev) => ({ ...prev, deathDate: '' }));
                }}
                onPrecisionChange={(p) => setPetFormData((prev) => ({ ...prev, deathDatePrecision: p }))}
                onErrorClear={() => setErrors((prev) => ({ ...prev, deathDate: '' }))}
                disabled={isSubmitting}
                error={errors.deathDate}
                allowClearViaInput
                layout="inline"
                wrapperClassName="add-pet-screen__date-field"
              />
            </div>
          </div>
        )}
      </div>
      {additionalOwners && additionalOwners.rows.length > 0 && (
        <div className="add-pet-screen__owners-section">
          <h3 className="person-edit-form__section-title">{t('pet.alsoAddAsOwners')}</h3>
          <p className="person-edit-form__hint add-pet-screen__owners-hint">
            Anyone else in the household who shared ownership of this pet?
          </p>
          <div className="add-pet-screen__owners-list">
            {additionalOwners.rows.map(({ ownerFamilyMemberId, displayName }) => {
              const choice = additionalOwners.choices[ownerFamilyMemberId] ?? {
                selected: false,
                role: 'secondary' as const
              };
              return (
                <div
                  key={ownerFamilyMemberId}
                  className={`add-pet-screen__owner-row ${choice.selected ? 'add-pet-screen__owner-row--selected' : ''}`}
                >
                  <label className="person-edit-form__checkbox-label add-pet-screen__owner-checkbox">
                    <input
                      type="checkbox"
                      checked={choice.selected}
                      onChange={(e) => additionalOwners.onToggle(ownerFamilyMemberId, e.target.checked)}
                      disabled={isSubmitting}
                      className="person-edit-form__checkbox"
                    />
                    <span className="add-pet-screen__owner-name">{displayName}</span>
                  </label>
                  {choice.selected && (
                    <Select.Root
                      value={choice.role}
                      onValueChange={(v) =>
                        additionalOwners.onRoleChange(ownerFamilyMemberId, v as 'primary' | 'secondary' | 'caregiver')
                      }
                      disabled={isSubmitting}
                    >
                      <Select.Trigger className="form-select-trigger" aria-label={`Role for ${displayName}`}>
                        <Select.Value />
                      </Select.Trigger>
                      <Select.Portal>
                        <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                          {(['primary', 'secondary', 'caregiver'] as const).map((r) => (
                            <Select.Item key={r} value={r} className="form-select-item">
                              <Select.ItemText>{PET_OWNER_ROLE_LABELS[r]}</Select.ItemText>
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Portal>
                    </Select.Root>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddPetCreateNewFields;
